import { AfterContentInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Client } from 'src/app/domain/client';
import { Contract } from 'src/app/domain/contract';
import { User } from 'src/app/domain/user';
import { ENavigationRoute } from 'src/app/model/enum/navigation-route';
import { UserConfigurationService } from 'src/app/services/configuration.service';
import { CoreData } from 'src/app/tools/core-data/core-data';
import { StringTool } from 'src/app/tools/mapping/string.tool';
import { ClientSelectionBoxDialog } from 'src/app/tools/ui/client-selection-box/client-selection-box';
import { LogAsBoxComponent } from 'src/app/tools/ui/log-as-box/log-as-box.component';
import { NotificationManager } from 'src/app/tools/ui/notification-manager';
import { UserBoxComponent } from 'src/app/tools/ui/user-box/user-box.component';

@Component({
    selector: 'app-user-configuration',
    templateUrl: './user-configuration.component.html',
    styleUrls: ['./user-configuration.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserConfigurationComponent implements OnInit, AfterContentInit {
    private subject: Subject<string> = new Subject();
    SearchedUser: string;
    UserList: User[] = [];
    SelectedUser: User;
    ClientList: Client[] = [];

    constructor(
        private coreData: CoreData,
        private router: Router,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private userConfigurationService: UserConfigurationService
    ) { }

    ngOnInit(): void {
    }

    ngAfterContentInit(): void {

        this.subject.pipe(
            debounceTime(500)
        ).subscribe(() =>
            this.searchClientList()
        );
    }

    get CanSearch() {
        return StringTool.isNotNullOrEmpty(this.SearchedUser);
    }

    onKeyUp() {
        if (!this.SearchedUser || this.SearchedUser.length < 3) {
            this.UserList = [];
            return;
        }

        this.subject.next();
    }

    searchClientList() {
        this.UserList = [];
        this.userConfigurationService.getAllUsers().subscribe(x => {
            this.UserList = x.filter(y => y.Email.includes(this.SearchedUser));
        });
    }

    assignSelectedUser(event: MatAutocompleteSelectedEvent) {
        this.SelectedUser = this.UserList.find(x => x.Email === event.option.value.Email);
    }

    deleteClient(client: Client) {
        this.userConfigurationService.deleteClientFromUser(client, this.SelectedUser).subscribe(x => {
            if (x) {
                _.remove(this.SelectedUser.ClientList, x => x.Code === client.Code);
                NotificationManager.handleSuccess(`Client retiré de ${this.SelectedUser.Email}`, this.snackBar);
            }
        })
    }

    deleteContract(contract: Contract, client: Client) {
        this.userConfigurationService.deleteContractFromUser(contract, client, this.SelectedUser).subscribe(x => {
            if (x) {
                _.remove(this.SelectedUser.ClientList.find(c => c.Code === client.Code).ContractList, x => x.Code === contract.Code);
                NotificationManager.handleSuccess(`Contrats retiré de ${this.SelectedUser.Email}`, this.snackBar);
            }
        })
    }

    public compareWith(m1: User, m2: User) {
        return m1 && m2 ? m1.Email === m2.Email : m1 === m2;
    }

    displayFn(user: User | string) {
        if (!user) {
            return '';
        }
        if (user instanceof User) {
            return user ? user.Email : '';
        }
        if (typeof (user) === "string") {
            return user;
        }
    }

    openClientSelectionBoxForNewClient() {
        const dialogRef = this.dialog.open(ClientSelectionBoxDialog, {
            width: '777px',
            data: {
                alreadyAssignedList: this.SelectedUser.ClientList
            }
        });

        dialogRef.afterClosed().subscribe((result: Client[]) => {
            if (result) {
                result.forEach(y => {
                    this.userConfigurationService.addClientToUser(y, this.SelectedUser).subscribe(x => {
                        this.SelectedUser.ClientList.push(y);
                        NotificationManager.handleSuccess(`Client ajouté à l\'utilisateur ${this.SelectedUser.Email}`, this.snackBar);
                    });
                })
            }
        });
    }

    openClientSelectionBoxForNewContracts(client: Client) {
        const dialogRef = this.dialog.open(ClientSelectionBoxDialog, {
            width: '777px',
            data: {
                client: client,
            }
        });

        dialogRef.afterClosed().subscribe((result: Contract[]) => {
            if (result) {
                result.forEach(y => {
                    this.userConfigurationService.addContractToUser(y, client, this.SelectedUser).subscribe(z => {
                        client.ContractList.push(y);
                        NotificationManager.handleSuccess(`Contrats ajoutés à l\'utilisateur ${this.SelectedUser.Email}`, this.snackBar);
                    });
                });
            }
        });
    }

    openCreationBox(user?: User) {
        const dialogRef = this.dialog.open(UserBoxComponent, {
            data: user,
            width: '555px',
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }
            if (user) {
                this.userConfigurationService.updateUser(result).subscribe(x => {
                    NotificationManager.handleSuccess('Utilisateur modifié !', this.snackBar);
                });
                return;
            }
            this.userConfigurationService.createUser(result).subscribe(x => {
                NotificationManager.handleSuccess('Utilisateur ajouté !', this.snackBar);
            });
        });
    }

    openLogAsBox(email: string) {
        // const dialogRef = this.dialog.open(LogAsBoxComponent, {
        //     width: '555px',
        //     disableClose: true
        // });

        // dialogRef.afterClosed().subscribe(email => {
        //     if (!email) {
        //         return;
        //     }

            this.userConfigurationService.logInAs(email).subscribe(x => {
                // this.coreData.Token = x;
                this.coreData.ImpersonateAs = email;
                NotificationManager.handleSuccess(`Log In As ${email} Successful !`, this.snackBar);
                this.router.navigate([ENavigationRoute.Home]);
            });
        // });
    }

    deleteUser(user: User) {
        this.userConfigurationService.deleteUser(user).subscribe(x => {
            this.SelectedUser = null;
            NotificationManager.handleSuccess('Utilisateur supprimé !', this.snackBar);
        });
    }
}
